import React, { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { Grid, InputAdornment } from '@mui/material';
import { Autocomplete, Button, IconButton, TextField } from '@totalsoft/rocket-ui';
import { withRights } from 'providers/RightsConsumer';
import { addPropertyPrefix } from 'utils/propertyChangeAdapters';
import PJContainerWrapper from 'components/common/select/BrowserContainer/legalPartners/PJContainerWrapper';
import { emptyArray, emptyString } from 'constants/emptyConst';
import Search from '@mui/icons-material/Search';
import { Prompt } from 'react-router-dom';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';
import { GlobalRightsContext } from 'providers/GlobalRightsContext';
import PhoneNumberField from 'components/common/inputs/PhoneNumberTextField';

export const OfferAssetPJClientComponent = ({
  onPropertyChange,
  onUniqueIdBlur,
  t,
  client,
  validationInfo,
  rights,
  onSave,
  saving,
  isDirty,
  partnerTypeCode,
  setIsPartnerFromModal,
  processing,
  isClientLoaded,
  partnerTypeId
}) => {
  const { masterPartnerSearch } = useContext(GlobalRightsContext);
  const { dealerContactSegregation } = useContext(ApplicationFeaturesContext);
  const dealerContactInfos = client.dealerContactInfos?.filter(x => x.isActive) || emptyArray;
  const isValid = validationInfo.isValid;
  const generalData = client.generalData || {};
  const contactData = client.contactData || {};
  const contactDataValidationInfo = validationInfo.contactData || {};
  const [isBrowserOpen, setIsBrowserOpen] = useState(false);

  const CustomEndIcons = ({ onCustomClick }) => (
    <InputAdornment position='end'>
      <IconButton disabled={rights.readOnlyFull} color='primary' variant='text' size='tiny' onClick={onCustomClick}>
        <Search />
      </IconButton>
    </InputAdornment>
  );

  const loadDealerContacts = useCallback(
    input => {
      if (!input) {
        return Promise.resolve(dealerContactInfos);
      }
      return Promise.resolve(dealerContactInfos?.filter(x => x.personName.toLowerCase().includes(input.toLowerCase())));
    },
    [dealerContactInfos]
  );

  const onAutocompleteChange = onPropertyChange => suggestion => {
    if (suggestion) {
      onPropertyChange(suggestion.dealerContactInfoId ? suggestion.dealerContactInfoId : suggestion.personName);
    }
  };

  return (
    <Grid container spacing={2} px={1} mt={0}>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <Grid item xs={12} sm={12} md={6}>
        <PJContainerWrapper
          disabled={rights.readOnlyFull || !rights.financedAssetClientWrite || processing}
          label={t(`OfferEdit.Client.${partnerTypeCode}.PersonalIdentifier`)}
          onTextFieldChange={addPropertyPrefix('generalData', onPropertyChange)('uniqueId')}
          onBlur={() => onUniqueIdBlur(generalData.uniqueId)}
          value={generalData.uniqueId}
          iconDisabled={false}
          iconHidden={!masterPartnerSearch}
          error={!!validationInfo.generalData?.uniqueId}
          helperText={validationInfo.generalData?.uniqueId}
          isBrowserOpen={isBrowserOpen}
          setIsBrowserOpen={setIsBrowserOpen}
          partnerTypeId={partnerTypeId}
          hasCustomIcons
          customEndIcons={
            <CustomEndIcons
              onCustomClick={() => {
                setIsBrowserOpen(true);
                setIsPartnerFromModal(true);
              }}
            />
          }
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <TextField
          id={'offerAssetClientCard_pjPartner_companyName'}
          fullWidth
          value={generalData.partnerName || ''}
          name='partnerName'
          label={t('OfferEdit.Client.PJ.CompanyName')}
          onChange={addPropertyPrefix('generalData', onPropertyChange)('partnerName')}
          disabled={rights.readOnlyFull || !rights.financedAssetClientWrite || processing}
          error={validationInfo.generalData?.partnerName}
          helperText={validationInfo.generalData?.partnerName}
        />
      </Grid>
      {dealerContactSegregation ? (
        <Grid item xs={12} sm={12} md={6}>
          <Autocomplete
            id={'offerAssetClientCard_pjPartner_personName'}
            disabled={rights.readOnlyFull || !rights.financedAssetClientWrite || processing || !generalData.uniqueId}
            fullWidth
            value={contactData}
            valueKey='dealerContactInfoId'
            labelKey='personName'
            label={t('OfferEdit.Client.PJ.ContactData.PersonName')}
            loadOptions={loadDealerContacts}
            options={dealerContactInfos}
            onChange={onAutocompleteChange(addPropertyPrefix('contactData', onPropertyChange)('dealerContactInfoId'))}
            creatable={true}
            error={contactDataValidationInfo.personName ? true : false}
            helperText={contactDataValidationInfo.personName}
            cache={false}
          />
        </Grid>
      ) : (
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            id={'offerAssetClientCard_pjPartner_personName'}
            fullWidth
            value={contactData.personName || ''}
            error={contactDataValidationInfo.personName ? true : false}
            helperText={contactDataValidationInfo.personName}
            name='personName'
            label={t('OfferEdit.Client.PJ.ContactData.PersonName')}
            onChange={addPropertyPrefix('contactData', onPropertyChange)('personName')}
            disabled={rights.readOnlyFull || !rights.financedAssetClientWrite || processing}
          />
        </Grid>
      )}

      <Grid item xs={12} sm={12} md={6}>
        <PhoneNumberField
          id={'offerAssetClientCard_pjPartner_mobilePhone'}
          country={contactData.phoneCountryCode}
          fullWidth
          value={contactData.phone || ''}
          error={contactDataValidationInfo.phone ? true : false}
          helperText={contactDataValidationInfo.phone}
          name='phone'
          label={t('OfferEdit.Client.MobilePhone')}
          onChange={addPropertyPrefix('contactData', onPropertyChange)('phone')}
          disabled={
            rights.readOnlyFull ||
            !rights.financedAssetClientWrite ||
            processing ||
            (dealerContactSegregation && (!generalData.uniqueId || !contactData.personName))
          }
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <TextField
          id={'offerAssetClientCard_pjPartner_email'}
          fullWidth
          value={contactData.email || ''}
          error={contactDataValidationInfo.email ? true : false}
          helperText={contactDataValidationInfo.email}
          name='email'
          label={t('OfferEdit.Client.Email')}
          onChange={addPropertyPrefix('contactData', onPropertyChange)('email')}
          disabled={
            rights.readOnlyFull ||
            !rights.financedAssetClientWrite ||
            processing ||
            (dealerContactSegregation && (!generalData.uniqueId || !contactData.personName))
          }
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          id={'offerAssetClientCard_pjPartner_comment'}
          label={t('OfferEdit.Client.Comment')}
          multiline
          disabled={rights.readOnlyFull || processing}
          inputProps={{ maxLength: 255 }}
          rows={3}
          value={contactData.comment || emptyString}
          onChange={addPropertyPrefix('contactData', onPropertyChange)('comment')}
          variant='outlined'
          sx={{ mt: 1 }}
        />
      </Grid>

      {!rights.isImage && (
        <Grid item xs={12} mt={1}>
          <Button
            id={'offerAssetClientCard_pjPartner_saveBtn'}
            onClick={onSave}
            disabled={
              saving || !isValid || rights.readOnlyFull || !rights.financedAssetClientWrite || !isDirty || processing || !isClientLoaded
            }
          >
            {saving ? t('Saving') : t('Save')}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

OfferAssetPJClientComponent.propTypes = {
  t: PropTypes.func.isRequired,
  client: PropTypes.object.isRequired,
  isDirty: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onUniqueIdBlur: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  validationInfo: PropTypes.object.isRequired,
  rights: PropTypes.object.isRequired,
  partnerTypeCode: PropTypes.string.isRequired,
  setIsPartnerFromModal: PropTypes.func.isRequired,
  isPJ: PropTypes.bool,
  processing: PropTypes.bool,
  isClientLoaded: PropTypes.bool,
  onCustomClick: PropTypes.func,
  partnerTypeId: PropTypes.number.isRequired
};

export default compose(withTranslation())(withRights(OfferAssetPJClientComponent));
